import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { apiClient, apiClientTakeDown } from '../services/apiClient';
import { showToast } from '../store/toaster/action';
import {
  BulkTakedownStatusUpdateItem,
  CompleteTakedownType,
  CreateSocialMediaTakedownPayload,
  CreateSocialMediaTakedownType,
  CreateTakedownType,
  RecategorizeTakedownPayload,
} from '../helpers/types';
import {
  SingleTakedownDetail,
  TakedownStatus,
  TakedownStatusHistoryItem,
} from '../types/TakedownTypes';

// ---------------------------------------------------------------------------
// API functions (WHEEL-341b: relocated from the deleted src/store/takedown/
// slice - this file is now the single home for takedown server calls).
// ---------------------------------------------------------------------------

export const getTakedownStatusApi = async (payload?: {
  takedown_type: string;
}) => {
  let url = `v2/takedown/statuses`;
  if (payload?.takedown_type) {
    url += `?takedown_type=${payload.takedown_type}`;
  }
  const config = {
    method: 'GET',
    url,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const takedownStatusHistoryApi = async (payload: {
  takedown_id: string;
}) => {
  const config = {
    method: 'GET',
    url: `v2/takedown/${payload.takedown_id}/statuses`,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export interface TakedownListParams {
  threat_domain: string;
  target_domain: string;
  page: number;
  size: number;
  takedown_type?: string[] | string;
  since?: string;
  until?: string;
  status_names?: string[] | string;
  company_id?: (number | string)[];
}

export const getTakeDownListApi = async (
  payload: TakedownListParams,
  signal?: AbortSignal,
) => {
  let url = `v2/takedown/?threat_domain=${payload.threat_domain}&target_domain=${payload.target_domain}&page=${payload.page}&size=${payload.size}`;

  if (
    Array.isArray(payload.takedown_type) &&
    payload.takedown_type.length > 0
  ) {
    const takedownTypeQuery = payload.takedown_type
      .map((type: string) => `takedown_type=${type}`)
      .join('&');
    url += `&${takedownTypeQuery}`;
  } else if (
    typeof payload.takedown_type === 'string' &&
    payload.takedown_type
  ) {
    url += `&takedown_type=${payload.takedown_type}`;
  }

  if (payload.since && payload.until) {
    url += `&since=${payload.since}&until=${payload.until}`;
  }

  if (Array.isArray(payload.status_names) && payload.status_names.length > 0) {
    const statusNamesQuery = payload.status_names
      .map((name: string) => `status_names=${name}`)
      .join('&');
    url += `&${statusNamesQuery}`;
  } else if (typeof payload.status_names === 'string' && payload.status_names) {
    url += `&status_names=${payload.status_names}`;
  }

  if (payload.company_id && payload.company_id.length > 0) {
    const companyIdsQuery = payload.company_id
      .map((id: string | number) => `company_ids=${id}`)
      .join('&');
    url += `&${companyIdsQuery}`;
  }

  const config = {
    method: 'GET',
    url: url,
    ...(signal && { signal }),
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const createTakedownApi = async (data: CreateTakedownType) => {
  const config = {
    method: 'POST',
    url: `v2/takedown/accept`,
    data,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const bulkCreateTakedownsApi = async (items: CreateTakedownType[]) => {
  const config = {
    method: 'POST',
    url: `v2/takedown/accept/bulk-job`,
    data: { items },
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const completeTakedownApi = async (data: { id?: string }) => {
  const config = {
    method: 'POST',
    url: `v2/takedown/${data.id}/status`,
    data,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const getHubSportIdApi = async (threatName: string | null) => {
  const config = {
    method: 'GET',
    url: `domain/getwi?d=${threatName}`,
  };
  const res = await apiClient(config);
  return res;
};

export interface TakedownDetailParams {
  asset_id?: number;
  threat_id?: number;
}

export const getTakedownDetailApi = async (payload: TakedownDetailParams) => {
  const config = {
    method: 'GET',
    url: `v2/assets/${payload.asset_id}/threats/${payload.threat_id}/takedowns`,
  };
  return await apiClientTakeDown(config);
};

export const getTakedownListTypesApi = async () => {
  const config = {
    method: 'GET',
    url: `v1/takedown/takedown_types/`,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const getTakeDownCancellationReasons = async () => {
  const config = {
    method: 'GET',
    url: `v2/takedown/cancellation_reasons`,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const getTakeDownRejectionReasons = async () => {
  const config = {
    method: 'GET',
    url: `v2/takedown/rejection_reasons`,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const recategorizeTakedownApi = async (data: {
  takedown_id: string;
  takedown_type: string;
  platform?: string | null;
  threat_type?: string | null;
}) => {
  const { takedown_id, ...body } = data;
  const config = {
    method: 'PATCH',
    url: `v2/takedown/${takedown_id}/recategorize`,
    data: body,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const getSocialMediaPlatformsApi = async () => {
  const config = {
    method: 'GET',
    url: `v2/takedown/social_media/platforms`,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

export const createSocialMediaTakedownApi = async (
  data: CreateSocialMediaTakedownType,
) => {
  const config = {
    method: 'POST',
    url: `v2/takedown/social-media`,
    data,
  };
  const res = await apiClientTakeDown(config);
  return res;
};

// ---------------------------------------------------------------------------
// Queries (WHEEL-339 + WHEEL-341b)
// ---------------------------------------------------------------------------

export const useSingleTakedownQuery = (takedownId?: string) =>
  useQuery<SingleTakedownDetail>({
    queryKey: ['takedown', takedownId],
    queryFn: async () => {
      const response = await apiClientTakeDown({
        url: `v2/takedown/${takedownId}`,
        method: 'GET',
      });
      return response.data;
    },
    enabled: !!takedownId,
  });

/**
 * Takedown status catalogue (WHEEL-339). Previously fetched independently by
 * the takedown slice saga AND three ad-hoc hooks - once per expanded row.
 * Lookup data: long staleTime, shared cache.
 */
export const takedownStatusesQueryKey = ['takedown', 'statuses'];

export const fetchTakedownStatuses = async (): Promise<TakedownStatus[]> => {
  const response = await getTakedownStatusApi();
  return response?.data ?? [];
};

export const useTakedownStatusesQuery = (enabled = true) =>
  useQuery<TakedownStatus[]>({
    queryKey: takedownStatusesQueryKey,
    queryFn: fetchTakedownStatuses,
    staleTime: 5 * 60_000,
    enabled,
  });

export const takedownStatusHistoryQueryKey = (takedownId?: string) => [
  'takedown',
  takedownId,
  'statusHistory',
];

export const fetchTakedownStatusHistory = async (
  takedownId: string,
): Promise<TakedownStatusHistoryItem[]> => {
  const response = await takedownStatusHistoryApi({ takedown_id: takedownId });
  return response?.data ?? [];
};

export const useTakedownStatusHistoryQuery = (takedownId?: string) =>
  useQuery<TakedownStatusHistoryItem[]>({
    queryKey: takedownStatusHistoryQueryKey(takedownId),
    queryFn: () => fetchTakedownStatusHistory(takedownId as string),
    enabled: !!takedownId,
  });

/**
 * Takedown list (WHEEL-341b). Replaces the `takedown.takedownList` slice
 * cell. TakedownList.tsx is the only caller that mounts this with live
 * params - it snapshots the current filters into `params` only at explicit
 * trigger points (mount, Apply, Reset, clear-search, page change), matching
 * the former imperative-dispatch behaviour: typing in a filter field alone
 * never refetches. Bypass search and CSV export keep calling
 * `getTakeDownListApi` directly (with an AbortSignal) and, for bypass search,
 * write straight into this query's cache via `queryClient.setQueryData` so
 * the table (which reads this same query's data) updates as pages stream in.
 */
export const takedownListQueryKeyPrefix = ['takedown', 'list'] as const;

export const takedownListQueryKey = (params: TakedownListParams) => [
  ...takedownListQueryKeyPrefix,
  params,
];

export interface TakedownListResponse {
  items: any[];
  total: number;
}

export const useTakedownListQuery = (params: TakedownListParams | null) =>
  useQuery<TakedownListResponse>({
    queryKey: takedownListQueryKey(params ?? ({} as TakedownListParams)),
    queryFn: async () => {
      const response = await getTakeDownListApi(params as TakedownListParams);
      return response?.data ?? { items: [], total: 0 };
    },
    enabled: params !== null,
  });

/** Per-threat takedown history, shown in ExternalToolList / TakedownDetails /
 * SummaryDetail / ThreatExplainPanel. All four call this with the same
 * `{asset_id, threat_id}` key and share one cache entry. */
export const takedownDetailQueryKey = (assetId?: number, threatId?: number) => [
  'takedown',
  'detail',
  assetId,
  threatId,
];

export const useTakedownDetailQuery = (params: TakedownDetailParams) =>
  useQuery<{ items: any[]; total?: number }>({
    queryKey: takedownDetailQueryKey(params?.asset_id, params?.threat_id),
    queryFn: async () => {
      const response = await getTakedownDetailApi(params);
      return response?.data ?? { items: [] };
    },
    enabled: !!params?.asset_id && !!params?.threat_id,
  });

const TAKEDOWN_TYPES_FALLBACK = [
  'identified_threat_domain',
  'external_threat_domain',
  'social_media',
];

export const useTakedownTypesQuery = () =>
  useQuery<string[]>({
    queryKey: ['takedown', 'types'],
    queryFn: async () => {
      const response = await getTakedownListTypesApi();
      return response?.data ?? [];
    },
    placeholderData: TAKEDOWN_TYPES_FALLBACK,
    staleTime: 5 * 60_000,
  });

/** Fires the HubSpot "get website info" lookup for a confirmed threat. The
 * response isn't consumed by any UI (it wasn't under Redux either) - the
 * call itself is the side effect the backend relies on. */
export const useHubspotIdQuery = (threatName?: string | null, enabled = true) =>
  useQuery({
    queryKey: ['takedown', 'hubspotId', threatName],
    queryFn: async () => {
      const response = await getHubSportIdApi(threatName ?? null);
      return response?.data;
    },
    enabled: enabled && !!threatName,
  });

export const socialMediaPlatformsQueryKey = [
  'takedown',
  'socialMediaPlatforms',
];

export const useSocialMediaPlatformsQuery = (enabled = true) =>
  useQuery<string[]>({
    queryKey: socialMediaPlatformsQueryKey,
    queryFn: async () => {
      const response = await getSocialMediaPlatformsApi();
      return response?.data?.items ?? [];
    },
    staleTime: 5 * 60_000,
    enabled,
  });

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const getTakedownErrorMessage = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail[0]?.msg || fallback;
  if (typeof detail === 'object' && detail !== null) {
    return detail.message || fallback;
  }
  return detail || fallback;
};

/** Initiates a takedown for a single threat. Does not refetch the takedown
 * detail query - the former saga didn't either (matching CREATE_TAKEDOWN_*,
 * only ExternalToolList's own confirmed-status effect was refetching, and
 * that was gated on completeTakedown, not createTakedown). */
export const useCreateTakedownMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: CreateTakedownType) => {
      const response = await createTakedownApi(payload);
      return response?.data;
    },
    onSuccess: () => {
      dispatch(showToast('Takedown created successfully', 'success'));
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          getTakedownErrorMessage(error, 'Failed to initiate takedown'),
          'error',
        ),
      );
    },
  });
};

/** Initiates takedowns for many threats in one request (WHEEL-195).
 * POST v2/takedown/accept/bulk-job is fire-and-forget: the server answers
 * immediately and per-item results arrive on the `bulk_takedown` WS channel
 * (see BulkTakedownUpdatePayload). The server rejects requests above 100
 * items, so larger selections are sent as sequential chunks. No success
 * toast here - the caller owns progress/completion feedback; only
 * request-level failures toast. */
export const MAX_BULK_TAKEDOWN_ITEMS = 100;

export const useBulkCreateTakedownsMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (items: CreateTakedownType[]) => {
      for (let i = 0; i < items.length; i += MAX_BULK_TAKEDOWN_ITEMS) {
        await bulkCreateTakedownsApi(
          items.slice(i, i + MAX_BULK_TAKEDOWN_ITEMS),
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          getTakedownErrorMessage(error, 'Failed to initiate takedowns'),
          'error',
        ),
      );
    },
  });
};

export const bulkUpdateTakedownStatusApi = async (
  items: BulkTakedownStatusUpdateItem[],
) => {
  const config = {
    method: 'POST',
    url: `v2/takedown/bulk-status`,
    data: { items },
  };
  const res = await apiClientTakeDown(config);
  return res;
};

/** Updates the status of many takedowns in one request (WHEEL-356).
 * POST v2/takedown/bulk-status is fire-and-forget: the server answers
 * immediately and per-item results arrive on the `takedown_status_update`
 * WS channel (see TakedownStatusUpdateSocketPayload). No success toast or
 * list invalidation here - the caller owns progress/completion feedback;
 * only request-level failures toast. */
export const useBulkUpdateTakedownStatusMutation = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (items: BulkTakedownStatusUpdateItem[]) => {
      const response = await bulkUpdateTakedownStatusApi(items);
      return response?.data;
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          getTakedownErrorMessage(error, 'Failed to update takedown statuses'),
          'error',
        ),
      );
    },
  });
};

/**
 * Updates a takedown's status (complete/cancel/reject/etc). The former saga
 * had two distinct refetch paths depending on caller: TakedownTable set
 * `takedownPage: true` and the saga re-fetched the takedown LIST;
 * ExternalToolList passed `asset_id`/`threat_id` and relied on its own
 * `isSucessTakeDown`-triggered effect to refetch the per-threat DETAIL.
 * Both are unified here: invalidate whichever key applies to the caller.
 */
export const useCompleteTakedownMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: CompleteTakedownType) => {
      const apiPayload = { ...payload };
      delete apiPayload.takedown_type;
      delete apiPayload.takedownPage;
      delete apiPayload.threat_domain;
      delete apiPayload.target_domain;
      delete apiPayload.company_id;
      delete apiPayload.status_names;
      const response = await completeTakedownApi(apiPayload);
      return response?.data;
    },
    onSuccess: (data, variables) => {
      dispatch(
        showToast(
          data?.message || 'Takedown completed successfully',
          'success',
        ),
      );
      if (variables.takedownPage) {
        queryClient.invalidateQueries({ queryKey: takedownListQueryKeyPrefix });
      }
      if (variables.asset_id && variables.threat_id) {
        queryClient.invalidateQueries({
          queryKey: takedownDetailQueryKey(
            variables.asset_id,
            variables.threat_id,
          ),
        });
      }
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          typeof error?.response?.data?.detail === 'string'
            ? error.response.data.detail
            : 'Failed to Update takedown',
          'error',
        ),
      );
    },
  });
};

/** Reclassifies a takedown between social_media / external_threat_domain.
 * Only used from TakedownTable, which always sets `takedownPage: true`. */
export const useRecategorizeTakedownMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: async (payload: RecategorizeTakedownPayload) => {
      const response = await recategorizeTakedownApi(payload.apiPayload);
      return response?.data;
    },
    onSuccess: (data, variables) => {
      dispatch(
        showToast(
          data?.message || 'Takedown reclassified successfully',
          'success',
        ),
      );
      if (variables.filterParams?.takedownPage) {
        queryClient.invalidateQueries({ queryKey: takedownListQueryKeyPrefix });
      }
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          typeof error?.response?.data?.detail === 'string'
            ? error.response.data.detail
            : 'Failed to reclassify takedown',
          'error',
        ),
      );
    },
  });
};

export interface SocialMediaTakedownProgress {
  current: number;
  total: number;
}

/**
 * Creates one social-media takedown per malicious URL (one POST per URL,
 * matching e2e/socialMediaTakedown.spec.ts). Reimplemented as a hook wrapping
 * `useMutation` + local `progress` state (rather than a plain mutation)
 * because SocialMediaTakedownModal needs `{current, total}` updates *between*
 * POSTs, not just a single pending/settled boolean. The mutationFn closes
 * over `setProgress` (declared in the same hook) to report progress after
 * each URL settles - preserving the saga's per-URL loop, partial-failure
 * toast, and list refetch exactly.
 */
export const useCreateSocialMediaTakedownMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [progress, setProgress] = useState<SocialMediaTakedownProgress | null>(
    null,
  );

  const mutation = useMutation({
    mutationFn: async (payload: CreateSocialMediaTakedownPayload) => {
      const { malicious_urls, ...basePayload } = payload.apiPayload;
      const errors: string[] = [];

      for (let i = 0; i < malicious_urls.length; i++) {
        try {
          await createSocialMediaTakedownApi({
            ...basePayload,
            malicious_url: malicious_urls[i],
          });
        } catch (error: any) {
          const msg =
            typeof error?.response?.data?.detail === 'string'
              ? error.response.data.detail
              : error?.response?.data?.detail?.[0]?.msg || malicious_urls[i];
          errors.push(msg);
        }
        setProgress({ current: i + 1, total: malicious_urls.length });
      }

      if (errors.length === malicious_urls.length) {
        throw new Error(`Failed to create takedowns: ${errors.join(', ')}`);
      }

      return { errors, total: malicious_urls.length };
    },
    onMutate: () => {
      setProgress(null);
    },
    onSuccess: ({ errors, total }) => {
      const successCount = total - errors.length;
      if (errors.length > 0) {
        dispatch(
          showToast(
            `${successCount} takedown(s) created. ${
              errors.length
            } failed: ${errors.join(', ')}`,
            'warning',
          ),
        );
      } else {
        dispatch(
          showToast(
            successCount === 1
              ? 'Social media takedown created successfully'
              : `${successCount} social media takedowns created successfully`,
            'success',
          ),
        );
      }
      queryClient.invalidateQueries({ queryKey: takedownListQueryKeyPrefix });
    },
    onError: (error: any) => {
      dispatch(
        showToast(
          error?.message || 'Failed to create social media takedown',
          'error',
        ),
      );
    },
    onSettled: () => {
      setProgress(null);
    },
  });

  return {
    mutate: mutation.mutate,
    isLoading: mutation.isPending,
    progress,
  };
};
